import { defineProperty as _defineProperty, inherits as _inherits, createClass as _createClass, classCallCheck as _classCallCheck, callSuper as _callSuper, objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { noop, debounce } from 'lodash-es';
import { cssClassPrefix, ENTER_KEY } from '../../shared/constants/generalConstants.js';
import { DEBOUNCE_MIN_CHARS_FOR_SEARCH, DEFAULT_OPTIONS_LIMIT, DEFAULT_NO_OPTION_TEXT, DEBOUNCE_DEFAULT_VALUE } from './constants.js';
import { IlIcon } from '../IlIcon/index.js';
import { IlSearchV2Dropdown } from './IlSearchV2Dropdown/index.js';
import { IlCancelDebounceError } from './IlCancelDebounceError/index.js';
import { withFrameworkTranslation } from '../../shared/localization/WithFrameworkTranslation.js';
import { getI18nInstance } from '../../shared/localization/i18n.js';

var BASE_CSS = "".concat(cssClassPrefix, "-search-v2");
var HANDLE_SEARCH_ORIGIN = {
  dropDown: 'fromDropDown',
  button: 'fromSearchButton',
  keyPress: 'fromKeyPress'
};
var IlSearchV2PropTypes = {
  className: PropTypes.string,
  type: PropTypes.string,
  small: PropTypes.bool,
  placeHolderText: PropTypes.string,
  labelText: PropTypes.string,
  id: PropTypes.string.isRequired,
  closeButtonLabelText: PropTypes.string,
  submitButtonLabelText: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  handleSearch: PropTypes.func,
  invalid: PropTypes.bool,
  invalidText: PropTypes.string,
  forceAutoCompleteOff: PropTypes.bool,
  tabIndexValue: PropTypes.number,
  hideControls: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({
    lens: PropTypes.bool,
    close: PropTypes.bool
  })]),
  debounceOptions: PropTypes.shape({
    onCallback: PropTypes.func,
    waitTime: PropTypes.number,
    minCharsLength: PropTypes.number
  }),
  dropdown: PropTypes.shape({
    loadOptions: PropTypes.func.isRequired,
    getOptionLabel: PropTypes.func,
    getOptionValue: PropTypes.func,
    highlightLabelText: PropTypes.bool,
    optionsLimit: PropTypes.number,
    noOptionsText: PropTypes.string,
    noOptionsHideDropdown: PropTypes.bool
  })
};
var IlSearchV2 = /*#__PURE__*/function (_Component) {
  function IlSearchV2(props) {
    var _this;
    _classCallCheck(this, IlSearchV2);
    _this = _callSuper(this, IlSearchV2, [props]);
    _defineProperty(_this, "state", {
      i18nInstance: getI18nInstance(),
      searchValue: _this.props.value || '',
      dropdownState: {
        options: [],
        isOptionSelected: false,
        dropdownOpen: false,
        loading: false,
        error: null
      }
    });
    _defineProperty(_this, "input", /*#__PURE__*/React__default.createRef());
    _defineProperty(_this, "wrapperRef", /*#__PURE__*/React__default.createRef());
    _defineProperty(_this, "handleDebounce", function () {
      var _this$state = _this.state,
        searchValue = _this$state.searchValue,
        dropdownState = _this$state.dropdownState;
      var _this$props = _this.props,
        _this$props$debounceO = _this$props.debounceOptions,
        _this$props$debounceO2 = _this$props$debounceO === void 0 ? {} : _this$props$debounceO,
        onCallback = _this$props$debounceO2.onCallback,
        _this$props$debounceO3 = _this$props$debounceO2.minCharsLength,
        minCharsLength = _this$props$debounceO3 === void 0 ? DEBOUNCE_MIN_CHARS_FOR_SEARCH : _this$props$debounceO3,
        _this$props$dropdown = _this$props.dropdown,
        _this$props$dropdown2 = _this$props$dropdown === void 0 ? {} : _this$props$dropdown,
        loadOptions = _this$props$dropdown2.loadOptions;
      if (!loadOptions && onCallback && (searchValue.length >= minCharsLength || !searchValue)) {
        _this.debounceSearchValue();
      }
      if (loadOptions && searchValue.length >= minCharsLength) {
        _this.setState({
          dropdownState: _objectSpread2(_objectSpread2({}, dropdownState), {}, {
            loading: true
          })
        });
        _this.debounceGetOptions();
      }
    });
    _defineProperty(_this, "clearInput", function (e) {
      var dropdownState = _this.state.dropdownState;
      var current = _this.input.current;
      var clearedEvt = _objectSpread2(_objectSpread2({}, e.target), {}, {
        target: {
          value: ''
        }
      });
      _this.handleChange(clearedEvt);
      _this.setState({
        searchValue: '',
        dropdownState: _objectSpread2(_objectSpread2({}, dropdownState), {}, {
          dropdownOpen: false,
          isOptionSelected: false
        })
      }, function () {
        return current.focus();
      });
    });
    _defineProperty(_this, "onSearch", function (option, origin) {
      var _this$state2 = _this.state,
        searchValue = _this$state2.searchValue,
        dropdownState = _this$state2.dropdownState;
      var handleSearch = _this.props.handleSearch;
      _this.setState({
        dropdownState: _objectSpread2(_objectSpread2({}, dropdownState), {}, {
          isOptionSelected: true,
          dropdownOpen: false
        })
      });
      handleSearch(searchValue, option, origin);
    });
    _defineProperty(_this, "handleKeyPress", function (e) {
      if (e.key === ENTER_KEY.VALUE) {
        _this.onSearch(undefined, HANDLE_SEARCH_ORIGIN.keyPress);
      }
    });
    _defineProperty(_this, "handleBlur", function () {
      var onBlur = _this.props.onBlur;
      onBlur();
    });
    _defineProperty(_this, "onSearchCallback", function () {
      var searchValue = _this.state.searchValue;
      var onCallback = _this.props.debounceOptions.onCallback;
      return onCallback(searchValue);
    });
    _defineProperty(_this, "handleChange", function (e) {
      var value = e.target.value;
      var _this$state3 = _this.state,
        searchValue = _this$state3.searchValue,
        dropdownState = _this$state3.dropdownState,
        dropdownOpen = _this$state3.dropdownState.dropdownOpen;
      var _this$props2 = _this.props,
        onChange = _this$props2.onChange,
        dropdown = _this$props2.dropdown,
        _this$props2$debounce = _this$props2.debounceOptions,
        _this$props2$debounce2 = _this$props2$debounce === void 0 ? {} : _this$props2$debounce,
        _this$props2$debounce3 = _this$props2$debounce2.minCharsLength,
        minCharsLength = _this$props2$debounce3 === void 0 ? DEBOUNCE_MIN_CHARS_FOR_SEARCH : _this$props2$debounce3;
      if (searchValue !== value) {
        _this.setState({
          searchValue: value
        }, function () {
          return _this.handleDebounce();
        });
      }
      if (dropdown) {
        if (!dropdownOpen && value.length >= minCharsLength) {
          _this.setState({
            dropdownState: _objectSpread2(_objectSpread2({}, dropdownState), {}, {
              dropdownOpen: true,
              isOptionSelected: false
            })
          });
        }
        if (dropdownOpen && value.length < minCharsLength) {
          _this.setState({
            dropdownState: _objectSpread2(_objectSpread2({}, dropdownState), {}, {
              dropdownOpen: false,
              isOptionSelected: false
            })
          });
        }
      }
      onChange(e);
    });
    _defineProperty(_this, "handleSelect", function (selectValue, option) {
      _this.setState({
        searchValue: selectValue
      }, function () {
        _this.onSearch(option, HANDLE_SEARCH_ORIGIN.dropDown);
        _this.input.current.focus();
      });
    });
    _defineProperty(_this, "onSearchButtonClick", function () {
      _this.onSearch(undefined, HANDLE_SEARCH_ORIGIN.button);
    });
    _defineProperty(_this, "handleClickOutside", function (event) {
      if (!_this.wrapperRef.current.contains(event.target)) {
        var dropdownState = _this.state.dropdownState;

        // Click outside of sidebar menu
        _this.setState({
          dropdownState: _objectSpread2(_objectSpread2({}, dropdownState), {}, {
            dropdownOpen: false
          })
        });
      }
    });
    _defineProperty(_this, "handleInputClick", function () {
      var _this$state4 = _this.state,
        searchValue = _this$state4.searchValue,
        dropdownState = _this$state4.dropdownState,
        dropdownOpen = _this$state4.dropdownState.dropdownOpen;
      var _this$props3 = _this.props,
        dropdown = _this$props3.dropdown,
        _this$props3$debounce = _this$props3.debounceOptions,
        _this$props3$debounce2 = _this$props3$debounce === void 0 ? {} : _this$props3$debounce,
        _this$props3$debounce3 = _this$props3$debounce2.minCharsLength,
        minCharsLength = _this$props3$debounce3 === void 0 ? DEBOUNCE_MIN_CHARS_FOR_SEARCH : _this$props3$debounce3;
      if (dropdown && searchValue.length >= minCharsLength) {
        _this.setState({
          dropdownState: _objectSpread2(_objectSpread2({}, dropdownState), {}, {
            dropdownOpen: !dropdownOpen,
            isOptionSelected: false
          })
        });
      }
    });
    _defineProperty(_this, "renderError", function (invalid, invalidText) {
      return invalid && /*#__PURE__*/React__default.createElement("div", {
        className: "".concat(BASE_CSS, "__errors")
      }, /*#__PURE__*/React__default.createElement("span", {
        className: "".concat(BASE_CSS, "--error")
      }, invalidText));
    });
    _defineProperty(_this, "getOptions", function () {
      var _this$props$dropdown3 = _this.props.dropdown,
        loadOptions = _this$props$dropdown3.loadOptions,
        noOptionsHideDropdown = _this$props$dropdown3.noOptionsHideDropdown;
      var _this$state5 = _this.state,
        searchValue = _this$state5.searchValue,
        dropdownState = _this$state5.dropdownState,
        isOptionSelected = _this$state5.dropdownState.isOptionSelected;
      loadOptions(searchValue).then(function (res) {
        return _this.setState({
          dropdownState: _objectSpread2(_objectSpread2({}, dropdownState), {}, {
            dropdownOpen: !(noOptionsHideDropdown && !res.length) && !isOptionSelected,
            loading: false,
            options: res,
            error: null
          })
        });
      })["catch"](function (err) {
        if (err instanceof IlCancelDebounceError) {
          // eslint-disable-next-line no-useless-return
          return;
          // eslint-disable-next-line no-else-return
        } else {
          _this.setState({
            dropdownState: _objectSpread2(_objectSpread2({}, dropdownState), {}, {
              loading: false,
              options: [],
              error: err
            })
          });
        }
      });
    });
    _defineProperty(_this, "renderDropdown", function () {
      var _this$state6 = _this.state,
        searchValue = _this$state6.searchValue,
        _this$state6$dropdown = _this$state6.dropdownState,
        dropdownOpen = _this$state6$dropdown.dropdownOpen,
        options = _this$state6$dropdown.options,
        loading = _this$state6$dropdown.loading;
      var _this$props$dropdown4 = _this.props.dropdown,
        _this$props$dropdown5 = _this$props$dropdown4 === void 0 ? {} : _this$props$dropdown4,
        loadOptions = _this$props$dropdown5.loadOptions,
        getOptionLabel = _this$props$dropdown5.getOptionLabel,
        getOptionValue = _this$props$dropdown5.getOptionValue,
        _this$props$dropdown6 = _this$props$dropdown5.highlightLabelText,
        highlightLabelText = _this$props$dropdown6 === void 0 ? false : _this$props$dropdown6,
        _this$props$dropdown7 = _this$props$dropdown5.optionsLimit,
        optionsLimit = _this$props$dropdown7 === void 0 ? DEFAULT_OPTIONS_LIMIT : _this$props$dropdown7,
        _this$props$dropdown8 = _this$props$dropdown5.noOptionsText,
        noOptionsText = _this$props$dropdown8 === void 0 ? DEFAULT_NO_OPTION_TEXT : _this$props$dropdown8;
      return loadOptions && dropdownOpen && /*#__PURE__*/React__default.createElement(IlSearchV2Dropdown, {
        searchValue: searchValue,
        options: options,
        loading: loading,
        getOptionLabel: getOptionLabel,
        getOptionValue: getOptionValue,
        highlightLabelText: highlightLabelText,
        optionsLimit: optionsLimit,
        noOptionsText: noOptionsText,
        handleSelect: _this.handleSelect
      });
    });
    _defineProperty(_this, "renderControls", function () {
      var _this$props4 = _this.props,
        closeButtonLabelText = _this$props4.closeButtonLabelText,
        submitButtonLabelText = _this$props4.submitButtonLabelText,
        tabIndexValue = _this$props4.tabIndexValue,
        hideControls = _this$props4.hideControls;
      var searchValue = _this.state.searchValue;
      var hideCloseIcon = hideControls.close,
        hideLensIcon = hideControls.lens;
      return /*#__PURE__*/React__default.createElement("div", {
        className: classNames(_defineProperty(_defineProperty(_defineProperty({}, "".concat(BASE_CSS, "__controls"), true), "".concat(BASE_CSS, "--close--hidden"), !searchValue || hideCloseIcon), "".concat(BASE_CSS, "--lens--hidden"), hideLensIcon))
      }, /*#__PURE__*/React__default.createElement("button", {
        className: "".concat(BASE_CSS, "--close ").concat(BASE_CSS, "--separator"),
        type: "button",
        tabIndex: tabIndexValue,
        "aria-label": closeButtonLabelText,
        onClick: _this.clearInput,
        "data-testid": searchValue ? 'visible' : 'hidden'
      }, /*#__PURE__*/React__default.createElement(IlIcon, {
        name: "close-v1",
        className: "".concat(BASE_CSS, "--icon")
      })), /*#__PURE__*/React__default.createElement("button", {
        className: "".concat(BASE_CSS, "--lens"),
        type: "button",
        tabIndex: tabIndexValue,
        "aria-label": submitButtonLabelText,
        onClick: _this.onSearchButtonClick
      }, /*#__PURE__*/React__default.createElement(IlIcon, {
        name: "search-v1",
        className: "".concat(BASE_CSS, "--icon")
      })));
    });
    var _props$debounceOption = props.debounceOptions,
      _props$debounceOption2 = _props$debounceOption === void 0 ? {} : _props$debounceOption,
      _props$debounceOption3 = _props$debounceOption2.waitTime,
      waitTime = _props$debounceOption3 === void 0 ? DEBOUNCE_DEFAULT_VALUE : _props$debounceOption3;
    _this.debounceSearchValue = debounce(_this.onSearchCallback, waitTime);
    _this.debounceGetOptions = debounce(_this.getOptions, waitTime);
    return _this;
  }
  _inherits(IlSearchV2, _Component);
  return _createClass(IlSearchV2, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      var _this2 = this;
      var i18nInstance = this.state.i18nInstance;
      var dropdown = this.props.dropdown;
      if (dropdown) {
        document.addEventListener('mousedown', this.handleClickOutside);
      }
      i18nInstance.changeLanguage(document.documentElement.lang).then(function () {
        _this2.setState({
          i18nInstance: i18nInstance
        });
      });
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      document.removeEventListener('mousedown', this.handleClickOutside);
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props5 = this.props,
        className = _this$props5.className,
        type = _this$props5.type,
        id = _this$props5.id,
        placeHolderText = _this$props5.placeHolderText,
        labelText = _this$props5.labelText,
        small = _this$props5.small,
        disabled = _this$props5.disabled,
        invalid = _this$props5.invalid,
        invalidText = _this$props5.invalidText,
        tabIndexValue = _this$props5.tabIndexValue,
        dropdown = _this$props5.dropdown,
        hideControls = _this$props5.hideControls,
        forceAutoCompleteOff = _this$props5.forceAutoCompleteOff;
      var _this$state7 = this.state,
        searchValue = _this$state7.searchValue,
        i18nInstance = _this$state7.i18nInstance;
      return /*#__PURE__*/React__default.createElement("div", {
        className: classNames(_defineProperty(_defineProperty(_defineProperty(_defineProperty({}, "".concat(BASE_CSS), true), "".concat(BASE_CSS, "--xl"), !small), "".concat(BASE_CSS, "--sm"), small), className, className)),
        ref: this.wrapperRef
      }, /*#__PURE__*/React__default.createElement("div", {
        role: "search",
        "aria-labelledby": "".concat(id, "-label"),
        className: classNames(_defineProperty(_defineProperty({}, "".concat(BASE_CSS, "-input-wrapper"), true), "".concat(BASE_CSS, "--disabled"), disabled))
      }, /*#__PURE__*/React__default.createElement("label", {
        id: "".concat(id, "-label"),
        htmlFor: id,
        className: "".concat(BASE_CSS, "__label")
      }, labelText), /*#__PURE__*/React__default.createElement("input", {
        type: type,
        className: "".concat(BASE_CSS, "__input"),
        value: searchValue,
        id: id,
        placeholder: placeHolderText || i18nInstance.t('searchPlaceholder'),
        onChange: this.handleChange,
        onBlur: this.handleBlur,
        onKeyPress: this.handleKeyPress,
        onClick: this.handleInputClick,
        tabIndex: tabIndexValue,
        disabled: disabled,
        ref: this.input,
        autoComplete: dropdown || forceAutoCompleteOff ? 'off' : 'on'
      }), !(typeof hideControls === 'boolean' && hideControls) && this.renderControls()), this.renderDropdown(), this.renderError(invalid, invalidText));
    }
  }]);
}(Component);
_defineProperty(IlSearchV2, "propTypes", IlSearchV2PropTypes);
_defineProperty(IlSearchV2, "defaultProps", {
  type: 'text',
  className: '',
  small: false,
  closeButtonLabelText: 'Clear search',
  submitButtonLabelText: 'Submit search',
  onChange: noop,
  onBlur: noop,
  handleSearch: noop,
  tabIndexValue: 0,
  hideControls: false
});
var TranslatedIlSearchV2 = withFrameworkTranslation()(IlSearchV2);
var TranslatedIlCancelDebounceError = withFrameworkTranslation()(IlCancelDebounceError);

export { TranslatedIlCancelDebounceError as IlCancelDebounceError, TranslatedIlSearchV2 as IlSearchV2, IlSearchV2PropTypes };
